import React, { useState, useEffect } from "react";
import { Icon } from "../../";
import CopyTimesDropdown from "./CopyTimesDropdown";

const ScheduleStep = ({ schedule, onScheduleChange, errors = {} }) => {
  const [localSchedule, setLocalSchedule] = useState(schedule || []);
  const [activeCopyDay, setActiveCopyDay] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Initialize schedule from prop only on mount
  useEffect(() => {
    if (schedule && schedule.length > 0) {
      setLocalSchedule(schedule);
    } else {
      const defaultSchedule = days.map(() => ({
        enabled: true,
        lapses: [
          { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } },
        ],
      }));
      setLocalSchedule(defaultSchedule);
    }
  }, []); // Only run on mount

  const toggleDayEnabled = (dayIndex) => {
    const newSchedule = localSchedule.map((day, idx) => {
      if (idx !== dayIndex) return day;
      return {
        ...day,
        enabled: !day.enabled,
      };
    });
    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
  };

  const addLapse = (dayIndex) => {
    const newSchedule = localSchedule.map((day, idx) => {
      if (idx !== dayIndex) return day;
      return {
        ...day,
        lapses: [
          ...day.lapses,
          { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } },
        ],
      };
    });
    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
  };

  const removeLapse = (dayIndex, lapseIndex) => {
    const day = localSchedule[dayIndex];
    if (day.lapses.length > 1) {
      const newSchedule = localSchedule.map((d, idx) => {
        if (idx !== dayIndex) return d;
        return {
          ...d,
          lapses: d.lapses.filter((_, lIdx) => lIdx !== lapseIndex),
        };
      });
      setLocalSchedule(newSchedule);
      onScheduleChange(newSchedule);
    }
  };

  const updateLapse = (dayIndex, lapseIndex, field, value) => {
    const newSchedule = localSchedule.map((day, idx) => {
      if (idx !== dayIndex) return day;

      return {
        ...day,
        lapses: day.lapses.map((lapse, lIdx) => {
          if (lIdx !== lapseIndex) return lapse;

          return {
            ...lapse,
            [field]: { ...value },
          };
        }),
      };
    });

    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
  };

  const toggleCopyTarget = (targetIndex) => {
    setCopyTargets((prev) =>
      prev.includes(targetIndex)
        ? prev.filter((i) => i !== targetIndex)
        : [...prev, targetIndex],
    );
  };

  const applyCopyToTargets = (sourceDayIndex) => {
    const source = localSchedule[sourceDayIndex];
    if (!source) return;
    const clonedLapses = (source.lapses || []).map((l) => ({
      open: { ...l.open },
      close: { ...l.close },
    }));

    const newSchedule = [...localSchedule];
    copyTargets.forEach((targetIndex) => {
      if (targetIndex === sourceDayIndex) return;
      const target = newSchedule[targetIndex] || { enabled: true, lapses: [] };
      newSchedule[targetIndex] = {
        ...target,
        enabled: source.enabled,
        lapses: clonedLapses,
      };
    });

    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
    setActiveCopyDay(null);
    setCopyTargets([]);
  };

  const validateLapses = (lapses) => {
    // Sort lapses by open time
    const sortedLapses = [...lapses].sort((a, b) => {
      const aMinutes = a.open.hour * 60 + a.open.minute;
      const bMinutes = b.open.hour * 60 + b.open.minute;
      return aMinutes - bMinutes;
    });

    for (let i = 0; i < sortedLapses.length - 1; i++) {
      const current = sortedLapses[i];
      const next = sortedLapses[i + 1];

      const currentEndMinutes = current.close.hour * 60 + current.close.minute;
      const nextStartMinutes = next.open.hour * 60 + next.open.minute;

      if (currentEndMinutes >= nextStartMinutes) {
        return {
          isValid: false,
          message: `Lapse ${i + 1} ends at ${formatTime(current.close)} but next lapse starts at ${formatTime(next.open)}. There must be at least 1 minute gap.`,
        };
      }
    }

    return { isValid: true };
  };

  const formatTime = (time) => {
    return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
  };

  const formatTimeForInput = (time) => {
    return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
  };

  const parseTimeFromInput = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return { hour: hours || 0, minute: minutes || 0 };
  };

  const handleTimeChange = (dayIndex, lapseIndex, field, timeString) => {
    const time = parseTimeFromInput(timeString);
    updateLapse(dayIndex, lapseIndex, field, time);
  };

  const getValidationError = (dayIndex) => {
    const daySchedule = localSchedule[dayIndex];
    if (!daySchedule || !daySchedule.enabled) return null;

    const validation = validateLapses(daySchedule.lapses);
    return validation.isValid ? null : validation.message;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="mb-3 text-base font-medium text-white sm:mb-4 sm:text-lg">
          Business Hours
        </h3>
        <p className="mb-4 text-xs text-gray-400 sm:mb-6 sm:text-sm">
          Configure your business hours for each day. You can add multiple time
          periods per day.
        </p>
      </div>

      {days.map((day, dayIndex) => {
        const daySchedule = localSchedule[dayIndex] || {
          enabled: true,
          lapses: [],
        };
        const validationError = getValidationError(dayIndex);

        return (
          <div
            key={day}
            className="rounded-lg border border-gray-600 bg-gray-700 p-3 sm:p-4"
          >
            <div className="relative mb-3 flex items-center justify-between sm:mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  onClick={() => toggleDayEnabled(dayIndex)}
                  className={`flex h-5 w-5 items-center justify-center rounded border-2 sm:h-6 sm:w-6 ${
                    daySchedule.enabled
                      ? "border-blue-500 bg-blue-500"
                      : "border-gray-400 bg-gray-600"
                  }`}
                >
                  {daySchedule.enabled && (
                    <Icon
                      name="Check"
                      className="h-3 w-3 text-white sm:h-4 sm:w-4"
                    />
                  )}
                </button>
                <span
                  className={`text-sm font-medium sm:text-base ${daySchedule.enabled ? "text-white" : "text-gray-400"}`}
                >
                  {day}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {daySchedule.enabled && (
                  <button
                    onClick={() => addLapse(dayIndex)}
                    className="flex items-center space-x-1 text-xs text-blue-400 hover:text-blue-300 sm:text-sm"
                  >
                    <Icon name="Plus" className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Add Time Period</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                )}

                <button
                  onClick={() =>
                    setActiveCopyDay(
                      activeCopyDay === dayIndex ? null : dayIndex,
                    )
                  }
                  className="rounded border border-gray-500 p-1.5 text-gray-300 hover:text-white sm:p-2"
                  title="Copy times to other days"
                >
                  <Icon name="Copy" className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>

                {activeCopyDay === dayIndex && (
                  <CopyTimesDropdown
                    days={days}
                    excludeIndex={dayIndex}
                    selectedTargets={copyTargets}
                    onToggleTarget={toggleCopyTarget}
                    onApply={() => applyCopyToTargets(dayIndex)}
                    onCancel={() => {
                      setActiveCopyDay(null);
                      setCopyTargets([]);
                    }}
                  />
                )}
              </div>
            </div>

            {daySchedule.enabled && (
              <div className="space-y-2 sm:space-y-3">
                {daySchedule.lapses.map((lapse, lapseIndex) => (
                  <div
                    key={lapseIndex}
                    className="flex flex-col gap-2 rounded-lg bg-gray-600 p-2 sm:flex-row sm:items-center sm:gap-4 sm:p-3"
                  >
                    <div className="flex-1">
                      <label className="mb-1 block text-xs text-gray-400">
                        Open
                      </label>
                      <input
                        type="time"
                        value={formatTimeForInput(lapse.open)}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "open",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-500 bg-gray-700 px-2 py-1.5 text-xs text-white focus:ring-2 focus:ring-blue-500 focus:outline-none sm:px-3 sm:py-2 sm:text-sm"
                      />
                    </div>

                    <div className="flex-1">
                      <label className="mb-1 block text-xs text-gray-400">
                        Close
                      </label>
                      <input
                        type="time"
                        value={formatTimeForInput(lapse.close)}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "close",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-500 bg-gray-700 px-2 py-1.5 text-xs text-white focus:ring-2 focus:ring-blue-500 focus:outline-none sm:px-3 sm:py-2 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center pt-4">
                      <button
                        onClick={() => removeLapse(dayIndex, lapseIndex)}
                        disabled={daySchedule.lapses.length === 1}
                        className={`p-1.5 transition-colors sm:p-2 ${
                          daySchedule.lapses.length > 1
                            ? "cursor-pointer text-red-400 hover:text-red-300"
                            : "cursor-not-allowed text-gray-400 opacity-50"
                        }`}
                        title={
                          daySchedule.lapses.length > 1
                            ? "Remove time period"
                            : "At least one time period required"
                        }
                      >
                        <Icon name="Trash2" className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {validationError && (
                  <div className="rounded border border-red-500 bg-red-900/20 p-2 text-xs text-red-400 sm:text-sm">
                    {validationError}
                  </div>
                )}
              </div>
            )}

            {!daySchedule.enabled && (
              <div className="text-xs text-gray-500 italic sm:text-sm">
                Closed on {day}
              </div>
            )}
          </div>
        );
      })}

      {errors.schedule && (
        <div className="rounded border border-red-500 bg-red-900/20 p-2 text-xs text-red-400 sm:text-sm">
          {errors.schedule}
        </div>
      )}
    </div>
  );
};

export default ScheduleStep;
